/**
 * Feature Flag System - Type Definitions
 * 
 * A unified, type-safe feature flag system for Helix 2000.
 * Supports both boolean flags and whitelist-based feature filtering.
 */

// =============================================================================
// CORE TYPES
// =============================================================================

/** Feature visibility mode */
export enum FeatureMode {
  /** Show all features where enabled = true */
  NORMAL = 'normal',
  /** Show ONLY whitelisted features (for testing/isolation) */
  WHITELIST = 'whitelist',
  /** Hide all features (emergency kill switch) */
  DISABLED = 'disabled',
}

/** Individual feature configuration */
export interface FeatureConfig {
  /** Whether this feature is enabled by default */
  enabled: boolean;
  /** Optional metadata for documentation */
  description?: string;
  /** Category for grouping (e.g., 'combat', 'ui', 'debug') */
  category?: string;
  /** Whether this feature requires specific permissions */
  requiresPermission?: string;
}

/** Feature set configuration for a domain/module */
export interface FeatureSetConfig {
  /** Display name of this feature set */
  name: string;
  /** Current mode for this feature set */
  mode: FeatureMode;
  /** When mode is WHITELIST, only these features are shown */
  whitelist: string[];
  /** All available features in this set */
  features: Record<string, FeatureConfig>;
}

/** Complete feature configuration for the entire application */
export type FeatureRegistry = Record<string, FeatureSetConfig>;

// =============================================================================
// EVENT TYPES
// =============================================================================

/** Event payload when a feature changes */
export interface FeatureChangeEvent {
  domain: string;
  feature: string;
  previousValue: boolean;
  newValue: boolean;
  timestamp: number;
}

/** Event payload when a domain mode changes */
export interface DomainModeChangeEvent {
  domain: string;
  previousMode: FeatureMode;
  newMode: FeatureMode;
  timestamp: number;
}

/** Event payload when whitelist changes */
export interface WhitelistChangeEvent {
  domain: string;
  previousWhitelist: string[];
  newWhitelist: string[];
  timestamp: number;
}

/** Listener function types */
export type FeatureChangeListener = (event: FeatureChangeEvent) => void;
export type DomainModeChangeListener = (event: DomainModeChangeEvent) => void;
export type WhitelistChangeListener = (event: WhitelistChangeEvent) => void;

// =============================================================================
// UTILITY TYPES
// =============================================================================

/** Options for checking feature visibility */
export interface CheckFeatureOptions {
  /** Ignore whitelist mode and check raw enabled value */
  ignoreWhitelist?: boolean;
  /** Default value if feature/domain not found */
  defaultValue?: boolean;
}

/** Result of a feature check with metadata */
export interface FeatureCheckResult {
  visible: boolean;
  reason: 'enabled' | 'whitelisted' | 'disabled' | 'not-found' | 'mode-disabled';
  mode: FeatureMode;
  whitelisted: boolean;
}

// =============================================================================
// PRE-DEFINED DOMAINS (for type safety)
// =============================================================================

/** Known feature domains in the game */
export type KnownFeatureDomain =
  | 'devtools'
  | 'game'
  | 'ui'
  | 'combat'
  | 'inventory'
  | 'chat'
  | 'map'
  | 'npc'
  | 'player'
  | 'quests'
  | string; // Allow custom domains

/** Feature keys for each domain (extend as needed) */
export interface DomainFeatureKeys {
  devtools: 
    | 'playerTab'
    | 'physicsTab'
    | 'classesTab'
    | 'npcsTab'
    | 'projectilesTab'
    | 'connectionsTab'
    | 'editorTab'
    | 'mapTab'
    | 'testTab';
  
  game:
    | 'combatSystem'
    | 'physicsEngine'
    | 'particleEffects'
    | 'soundEffects'
    | 'music'
    | 'multiplayer'
    | 'saveSystem';
  
  ui:
    | 'hud'
    | 'minimap'
    | 'chatWindow'
    | 'inventoryWindow'
    | 'characterSheet'
    | 'skillTree'
    | 'questLog'
    | 'settingsMenu';
  
  combat:
    | 'targetingSystem'
    | 'autoAttack'
    | 'skills'
    | 'comboSystem'
    | 'damageNumbers'
    | 'criticalHits'
    | 'statusEffects';
  
  inventory:
    | 'equipItems'
    | 'useItems'
    | 'dropItems'
    | 'tradeItems'
    | 'crafting'
    | 'itemTooltips';
  
  chat:
    | 'publicChat'
    | 'privateMessages'
    | 'guildChat'
    | 'systemMessages'
    | 'emotes'
    | 'chatCommands';
  
  map:
    | 'worldMap'
    | 'fastTravel'
    | 'waypoints'
    | 'levelTransitions';
  
  npc:
    | 'dialogue'
    | 'quests'
    | 'shops'
    | 'services'
    | 'mobSpawning';
  
  player:
    | 'movement'
    | 'jumping'
    | 'crouching'
    | 'interaction'
    | 'emotes'
    | 'characterCustomization';
  
  quests:
    | 'questTracking'
    | 'questLog'
    | 'questMarkers'
    | 'rewards';
}

/** Helper type to get feature keys for a domain */
export type FeatureKeyFor<T extends keyof DomainFeatureKeys> = DomainFeatureKeys[T];
